import { useAtom, useSetAtom } from "jotai";
import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import {
    ITranslationKey,
    IInsertTranslationRequest,
    IUpdateTranslationRequest,
    ITranslation,
    translationKeysAtom,
    translationsLoadingAtom
} from "./types";

type SetTranslationKeyType = (payload: ITranslationKey[]) => void;
type FetchTranslationKeysType = () => void;
type DeleteTranslationKeyType = (key_id: number) => Promise<boolean>;
type AddTranslationKeyType = (insertType: IInsertTranslationRequest) => void;
type UpdateTranslationKeyType = (updateType: IUpdateTranslationRequest) => void;
type GetTranslationsByKeyType = (key_id: number) => Promise<ITranslation[] | null>;
type GetTranslationsByLanguageId = (language_id: number) => Promise<{ [key: string]: string } | null>;

type TranslationKeyFunctions = {
    get: FetchTranslationKeysType;
    delete: DeleteTranslationKeyType;
    add: AddTranslationKeyType;
    update: UpdateTranslationKeyType;
    getTranslationsByKey: GetTranslationsByKeyType;
    getTranslationsByLanguageId: GetTranslationsByLanguageId
}

type UseTranslationlanguageKeys = [
    ITranslationKey[],
    SetTranslationKeyType,
    TranslationKeyFunctions
];

export const useTranslationLanguageKeys = (): UseTranslationlanguageKeys => {
    const [translationKeys, setTranslationKeys] = useAtom(translationKeysAtom);
    const setTranslationsLoading = useSetAtom(translationsLoadingAtom);

    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const getTranslationKeys = async (): Promise<ITranslationKey[] | null> => {
        setTranslationsLoading(true);
        if (!authToken) return null;
        return await axios.get(environment.translationKeysUrl, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        })
            .then((response) => {
                if (response.data) {
                    return response.data;
                }
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: 'There was an issue while getting translation keys. Please try again later.'
                });
                return null;
            })
            .catch((err: any) => errorCatcher(err, loginFunctions.logout))
            .finally(() => setTranslationsLoading(false));
    }

    const deleteTranslationKey = async (translation_key_id: number): Promise<boolean> => {
        const deleteResponse: AxiosResponse<boolean> | void = await axios.delete(`${environment.translationKeysUrl}?translation_key_id=${translation_key_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (deleteResponse) {
            return deleteResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an issue while deleting a translation key. Please try again later.'
            });
            return false;
        }
    }

    const addTranslationKey = async (inputType: IInsertTranslationRequest): Promise<ITranslationKey | null> => {
        const insertResponse: AxiosResponse<ITranslationKey> | void = await axios.post(`${environment.translationUrl}`, inputType, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (insertResponse) {
            return insertResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an issue while adding a translation key. Please try again later.'
            });
            return null;
        }
    }

    const updateTranslationKey = async (updateType: IUpdateTranslationRequest): Promise<ITranslationKey | null> => {
        const updateResponse: AxiosResponse<ITranslationKey> | void = await axios.put(`${environment.translationUrl}`, updateType, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (updateResponse) {
            return updateResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an issue while updating a translation key. Please try again later.'
            });
            return null;
        }
    }

    const getTranslationsByKey = async (key_id: number): Promise<ITranslation[] | null> => {
        const getResponse: AxiosResponse<ITranslation[]> | void = await axios.get(`${environment.translationUrl}?key_id=${key_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (getResponse) {
            return getResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an issue while getting a specific translation key. Please try again later.'
            });
            return null;
        }
    }

    const getTranslationsByLanguageId = async (language_id: number): Promise<{ [key: string]: string } | null> => {
        if (!authToken) return null;
        const getTranslationsResponse: AxiosResponse<{ [key: string]: string }> | void = await axios.get(`${environment.translationUrl}?language_id=${language_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (getTranslationsResponse) {
            return getTranslationsResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an issue while getting a translation response. Please try again later.'
            });
            return null;
        }
    }

    const getTranslations = async () => {
        const result = await getTranslationKeys();
        if (result) {
            setTranslationKeys(result);
        }
    }

    const deleteKey = async (key_id: number) => {
        const result = await deleteTranslationKey(key_id);
        setTranslationKeys(translationKeys.filter((item: ITranslationKey) => item.id !== key_id));
        return result;
    }

    const addKey = async (inputType: IInsertTranslationRequest): Promise<void> => {
        const value = await addTranslationKey(inputType);
        if (value) {
            setTranslationKeys([...translationKeys, value]);
        }
    }

    const updateKey = async (updateType: IUpdateTranslationRequest): Promise<void> => {
        const result = await updateTranslationKey(updateType);
        if (result) {
            setTranslationKeys(translationKeys.map((item: ITranslationKey) => {
                if (item.id === updateType.translation_key.id) {
                    return updateType.translation_key
                }
                return item;
            }));
        }
    }

    const keyFunctions: TranslationKeyFunctions = {
        add: addKey,
        delete: deleteKey,
        get: getTranslations,
        update: updateKey,
        getTranslationsByKey: getTranslationsByKey,
        getTranslationsByLanguageId: getTranslationsByLanguageId
    }

    return [
        translationKeys,
        setTranslationKeys,
        keyFunctions
    ]
}
